import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  IconButton,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  School as SchoolIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  AttachMoney as MoneyIcon,
  Assignment as AssignmentIcon,
  Book as BookIcon,
  Notifications as NotificationsIcon,
  Logout as LogoutIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  Sms as SmsIcon,
  Assessment as ReportIcon,
  AccountBalance as AccountingIcon,
  Star as StarIcon,
  LocalHospital as HospitalIcon,
  AssignmentTurnedIn as LogBookIcon,
  Hotel as HostelIcon,
  Groups as ClassStudentsIcon,
  CalendarMonth as CalendarIcon,
  Schedule as TimetableIcon,
  PhoneAndroid as AppsIcon,
  Lock as LockIcon,
  PhotoLibrary as GalleryIcon,
  LocalLibrary as LibraryIcon,
  HowToReg as AdmissionIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import ChangePasswordDialog from './ChangePasswordDialog';
import { COLLEGE_NAME, COLLEGE_SHORT } from '../branding';

const drawerWidth = 260;

const menuItems = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/admin' },
  { text: 'Admission Inquiries', icon: <AdmissionIcon />, path: '/admin/admissions' },
  { text: 'Programs & Batches', icon: <SchoolIcon />, path: '/admin/academic' },
  { text: 'Students', icon: <PeopleIcon />, path: '/admin/students' },
  { text: 'Class-wise Students', icon: <ClassStudentsIcon />, path: '/admin/class-students' },
  { text: 'Faculty', icon: <PersonIcon />, path: '/admin/teachers' },
  { text: 'Attendance', icon: <CheckCircleIcon />, path: '/admin/attendance' },
  { text: 'Timetable', icon: <TimetableIcon />, path: '/admin/timetable' },
  { text: 'Academic Calendar', icon: <CalendarIcon />, path: '/admin/calendar' },
  { text: 'Clinical Postings', icon: <HospitalIcon />, path: '/admin/clinical' },
  { text: 'Skills Log Book', icon: <LogBookIcon />, path: '/admin/logbook' },
  { text: 'Hostel', icon: <HostelIcon />, path: '/admin/hostel' },
  { text: 'Library', icon: <LibraryIcon />, path: '/admin/library' },
  { text: 'College Gallery', icon: <GalleryIcon />, path: '/admin/gallery' },
  { text: 'Fees', icon: <MoneyIcon />, path: '/admin/fees' },
  { text: 'Accounting', icon: <AccountingIcon />, path: '/admin/accounting' },
  { text: 'Examinations', icon: <AssignmentIcon />, path: '/admin/examinations' },
  { text: 'Assignments', icon: <BookIcon />, path: '/admin/homework' },
  { text: 'Notices', icon: <NotificationsIcon />, path: '/admin/notices' },
  { text: 'Faculty Feedback', icon: <StarIcon />, path: '/admin/ratings' },
  { text: 'Communication', icon: <SmsIcon />, path: '/admin/communication' },
  { text: 'Reports', icon: <ReportIcon />, path: '/admin/reports' },
  { text: 'Configuration', icon: <SettingsIcon />, path: '/admin/configuration' },
  { text: 'Mobile Apps & Link', icon: <AppsIcon />, path: '/admin/connect' },
];

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo Header */}
      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Avatar
          sx={{
            width: 45,
            height: 45,
            bgcolor: '#26a69a',
            boxShadow: '0 4px 14px rgba(38, 166, 154, 0.4)',
          }}
        >
          <HospitalIcon />
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            title={COLLEGE_NAME}
            sx={{
              fontWeight: 700,
              color: '#fff',
              fontSize: '1rem',
              letterSpacing: '0.3px',
              lineHeight: 1.25,
              whiteSpace: 'normal',
            }}
          >
            {COLLEGE_NAME}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.7rem' }}
          >
            Admin Console · {COLLEGE_SHORT}
          </Typography>
        </Box>
      </Box>

      {/* Navigation Menu */}
      <Box sx={{ flex: 1, py: 2, overflowY: 'auto' }}>
        <Typography
          variant="overline"
          sx={{
            px: 3,
            mb: 1,
            display: 'block',
            color: 'rgba(255,255,255,0.4)',
            fontSize: '0.65rem',
            fontWeight: 600,
            letterSpacing: '1px',
          }}
        >
          Main Menu
        </Typography>
        <List sx={{ px: 1.5 }}>
          {menuItems.map((item) => (
            <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  py: 1.2,
                  px: 2,
                  backgroundColor: isActive(item.path)
                    ? 'rgba(38, 166, 154, 0.2)'
                    : 'transparent',
                  border: isActive(item.path)
                    ? '1px solid rgba(38, 166, 154, 0.3)'
                    : '1px solid transparent',
                  '&:hover': {
                    backgroundColor: isActive(item.path)
                      ? 'rgba(38, 166, 154, 0.25)'
                      : 'rgba(255,255,255,0.05)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 40,
                    color: isActive(item.path)
                      ? '#26a69a'
                      : 'rgba(255,255,255,0.7)',
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  sx={{
                    '& .MuiListItemText-primary': {
                      color: isActive(item.path)
                        ? '#fff'
                        : 'rgba(255,255,255,0.8)',
                      fontWeight: isActive(item.path) ? 600 : 400,
                      fontSize: '0.9rem',
                    },
                  }}
                />
                {isActive(item.path) && (
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: '#26a69a',
                      boxShadow: '0 0 10px #26a69a',
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {/* User Info & Logout */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255,255,255,0.1)',
          backgroundColor: 'rgba(0,0,0,0.2)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            mb: 2,
            p: 1.5,
            borderRadius: 2,
            backgroundColor: 'rgba(255,255,255,0.05)',
          }}
        >
          <Avatar
            sx={{
              width: 38,
              height: 38,
              bgcolor: '#00796b',
              fontSize: '0.9rem',
              fontWeight: 600,
            }}
          >
            {(user?.fullName || user?.username || 'A').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user?.fullName || user?.username || 'Admin'}
            </Typography>
            <Chip
              icon={<AdminIcon sx={{ fontSize: 12, color: '#4caf50 !important' }} />}
              label={user?.role || 'ADMIN'}
              size="small"
              sx={{
                height: 20,
                fontSize: '0.65rem',
                bgcolor: 'rgba(76, 175, 80, 0.15)',
                color: '#4caf50',
                border: '1px solid rgba(76, 175, 80, 0.3)',
                '& .MuiChip-label': { px: 0.8 },
              }}
            />
          </Box>
        </Box>
        <ListItemButton
          onClick={() => setPwOpen(true)}
          sx={{
            borderRadius: 2, py: 0.8, mb: 1, justifyContent: 'center',
            backgroundColor: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' },
          }}
        >
          <LockIcon sx={{ color: 'rgba(255,255,255,0.8)', mr: 1, fontSize: 18 }} />
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontWeight: 500, fontSize: '0.82rem' }}>Change password</Typography>
        </ListItemButton>
        <Tooltip title="Logout" placement="top">
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 2,
              py: 1,
              justifyContent: 'center',
              backgroundColor: 'rgba(244, 67, 54, 0.1)',
              border: '1px solid rgba(244, 67, 54, 0.2)',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.2)',
              },
            }}
          >
            <LogoutIcon sx={{ color: '#f44336', mr: 1, fontSize: 20 }} />
            <Typography
              sx={{ color: '#f44336', fontWeight: 500, fontSize: '0.85rem' }}
            >
              Logout
            </Typography>
          </ListItemButton>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f7fa' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: '#fff',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'none' }, color: '#004d40' }}
            >
              <MenuIcon />
            </IconButton>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                color: '#004d40',
                fontWeight: 600,
                fontSize: '1.2rem',
              }}
            >
              {menuItems.find((item) => isActive(item.path))?.text || 'Dashboard'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography
              variant="body2"
              sx={{ color: '#666', display: { xs: 'none', md: 'block' } }}
            >
              Welcome back,{' '}
              <span style={{ fontWeight: 600, color: '#004d40' }}>
                {user?.fullName || user?.username}
              </span>
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#004d40',
              backgroundImage: 'linear-gradient(180deg, #004d40 0%, #00251a 100%)',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: '#004d40',
              backgroundImage: 'linear-gradient(180deg, #004d40 0%, #00251a 100%)',
              border: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <Outlet />
      </Box>
      <ChangePasswordDialog open={pwOpen} onClose={() => setPwOpen(false)} />
    </Box>
  );
}
